import {
  useGetStudentPerformanceQuery,
  useGetStudentPaymentsQuery,
} from "../../../../Api/attoDeskApi";

// The RTK Query endpoints are typed as `IApiResponse` with
// `data: object | string` — the payload shape for /student-profile/performance
// and /student-profile/payments is not declared anywhere. Every existing
// consumer (PerformanceSection, PaymentSection, AssessmentManagement) casts
// with `as any` for the same reason. Keeping the cast here; the shapes below
// are the local structural typings that the same three consumers rely on.
type PerformanceOverall = {
  avgGrade?: number | null;
  attendancePct?: number | null;
  assignmentPct?: number | null;
};
type PaymentsSummary = {
  totalOutstanding?: number | null;
  totalPaid?: number | null;
  lastPaymentDate?: string | null;
};

export type StudentPerformanceSummary = {
  performancePct: number | null;   // rounded 0–100, null = no graded data or still loading
  attendancePct: number | null;    // rounded 0–100, null = no attendance recorded or still loading
  outstandingDue: number | null;   // dollars, null = payments endpoint returned no summary or still loading
  isLoading: boolean;
};

/**
 * Summary reader for the Student Detail stat strip. RTK Query dedupes by
 * cache key, so PerformanceSection and PaymentSection calling the same
 * endpoints hit the SAME cache entry — the stat card and the tab section
 * physically cannot see different numbers. Rounding matches the sections'
 * `Math.round(...)` calls so a 78.6 doesn't render as 78 on one surface
 * and 79 on the other.
 */
export function useStudentPerformanceSummary(
  studentId: string | number,
): StudentPerformanceSummary {
  const sid = String(studentId);
  const { data: perfData, isLoading: perfLoading } =
    useGetStudentPerformanceQuery({ studentId: sid });
  const { data: payData, isLoading: payLoading } =
    useGetStudentPaymentsQuery({ studentId: sid });

  const overall = ((perfData?.data as any)?.overall as PerformanceOverall | undefined);
  const summary = ((payData?.data as any)?.summary as PaymentsSummary | undefined);

  return {
    performancePct:
      overall?.avgGrade != null ? Math.round(overall.avgGrade) : null,
    attendancePct:
      overall?.attendancePct != null ? Math.round(overall.attendancePct) : null,
    outstandingDue:
      summary?.totalOutstanding != null ? Number(summary.totalOutstanding) : null,
    isLoading: perfLoading || payLoading,
  };
}
